/**
 * 사진대지 탭 컴포넌트
 * 설비별 일반 사진 관리 (점검항목과 무관)
 *
 * 저장 위치: MachineDocument.pictures[]
 * 용도: 설비 명판, 전경, 현장 상황 등 일반 사진
 *
 * 디자인: InspectionPhotoTab과 동일한 미니멀 스타일
 */

import { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { Camera, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { useMutation } from "@apollo/client";
import { createLogger } from "@ict/shared";
import { useToast } from "@/contexts/ToastContext";
import {
  UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION,
  generateClientMutationId,
  type UpdateMachineDocumentPicturesData,
  type UpdateMachineDocumentPicturesVariables,
} from "@/lib/graphql/equipment";
import { uploadOtherPhoto, type UploadResult } from "@/lib/upload";
import { InAppCamera } from "@/components/camera/InAppCamera";
import { base64ToFile, generatePhotoFileName } from "@/lib/camera-utils";
import { PhotoCaptureSlot } from "@/components/inspection/shared/PhotoCaptureSlot";
import { ImagePreviewModal } from "@/components/inspection/shared/ImagePreviewModal";
import { useFileCapture } from "@/hooks/useFileCapture";
import { HiddenFileInputs } from "@/components/inspection/shared/HiddenFileInputs";
import { mapSettledWithConcurrency } from "@/lib/concurrency";

const logger = createLogger({ module: "OtherPhotosTab" });

/** 업로드 동시성 제한 수 */
const UPLOAD_CONCURRENCY = 3;

/** 갤러리에서 한 번에 선택 가능한 최대 사진 수 */
const MAX_GALLERY_SELECT = 20;

/** 로컬/오프라인 URL 여부 확인 (서버 mutation에 포함하면 안 되는 URL) */
function isLocalUrl(url: string): boolean {
  return (
    url.startsWith("data:") ||
    url.startsWith("indexeddb://") ||
    url.startsWith("capacitor://") ||
    url.includes("/_capacitor_file_/")
  );
}

// ============================================================================
// Types & Interfaces
// ============================================================================

/** 업로드 진행률 */
interface UploadProgress {
  current: number;
  total: number;
}

interface OtherPhotosTabProps {
  pictures: string[];
  version: number;
  projectId: string;
  docKey: string;
  machineId: string;
  onUpdate: (pictures: string[], newVersion: number) => void;
  isLoading?: boolean;
}

// ============================================================================
// Sub Components
// ============================================================================

/** 로딩 상태 */
function LoadingState() {
  return (
    <div className="flex flex-col bg-gray-50 animate-pulse">
      {/* 헤더 스켈레톤 */}
      <div className="flex items-center justify-between border-b bg-white px-4 py-3">
        <div className="h-4 w-24 rounded bg-slate-200" />
      </div>
      {/* 사진 그리드 스켈레톤 */}
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="aspect-[4/3] rounded-xl bg-slate-200" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** 빈 목록 상태 - 사진대지 전용 EmptyState (useFileCapture hook으로 DRY) */
function EmptyState({
  onFileSelect,
  onCameraOpen,
  uploading,
  disabled,
  onMultiFileSelect,
  galleryMultiple,
}: {
  onFileSelect: (file: File) => void;
  onCameraOpen?: () => void;
  uploading?: boolean;
  disabled?: boolean;
  onMultiFileSelect?: (files: File[]) => void;
  galleryMultiple?: boolean;
}) {
  const {
    cameraInputRef,
    galleryInputRef,
    handleCameraClick,
    handleGalleryClick,
    handleFileChange,
    galleryMultiple: resolvedGalleryMultiple,
  } = useFileCapture({ onFileSelect, onCameraOpen, onMultiFileSelect, galleryMultiple });
  const isDisabled = disabled || uploading;

  return (
    <div className="flex flex-col items-center px-6 py-10">
      {/* 빈 프레임 일러스트 */}
      <div className="relative mb-6">
        {/* 뒤쪽 프레임 (살짝 회전) */}
        <div className="absolute -left-2 -top-1 h-28 w-24 rotate-[-6deg] rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/80" />
        {/* 앞쪽 프레임 */}
        <div className="relative z-10 flex h-28 w-24 rotate-[3deg] flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-white shadow-sm">
          {uploading ? (
            <Loader2 className="h-7 w-7 animate-spin text-blue-400" />
          ) : (
            <>
              <Camera className="h-7 w-7 text-slate-300" strokeWidth={1.5} />
              <div className="mt-2 flex gap-0.5">
                <div className="h-1 w-1 rounded-full bg-slate-200" />
                <div className="h-1 w-1 rounded-full bg-slate-200" />
                <div className="h-1 w-1 rounded-full bg-slate-200" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 안내 텍스트 */}
      <p className="mb-1 text-[15px] font-semibold text-slate-700">사진을 추가해 주세요</p>
      <p className="mb-6 text-xs text-slate-400">설비 명판, 전경, 현장 상황 등</p>

      {/* 액션 버튼 */}
      <div className="flex w-full max-w-[260px] flex-col gap-2.5">
        <button
          type="button"
          onClick={handleCameraClick}
          disabled={isDisabled}
          className="flex items-center justify-center gap-2 rounded-2xl bg-slate-800 px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-slate-800/20 transition-all duration-150 active:scale-[0.97] active:shadow-md disabled:opacity-40"
          aria-label="사진 촬영"
        >
          <Camera className="h-[18px] w-[18px]" strokeWidth={2} />
          카메라로 촬영
        </button>
        <button
          type="button"
          onClick={handleGalleryClick}
          disabled={isDisabled}
          className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-600 transition-all duration-150 active:scale-[0.97] active:bg-slate-50 disabled:opacity-40"
          aria-label="갤러리에서 선택"
        >
          <ImagePlus className="h-[18px] w-[18px]" strokeWidth={2} />
          갤러리에서 선택
        </button>
      </div>

      <HiddenFileInputs
        cameraInputRef={cameraInputRef}
        galleryInputRef={galleryInputRef}
        onFileChange={handleFileChange}
        galleryMultiple={resolvedGalleryMultiple}
      />
    </div>
  );
}

/** 사진 썸네일 카드 */
function PhotoCard({
  url,
  index,
  onDelete,
  onPreview,
  disabled,
}: {
  url: string;
  index: number;
  onDelete: () => void;
  onPreview: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="relative" data-testid={`photo-card-${index}`}>
      <div className="relative overflow-hidden rounded-xl">
        <img
          src={url}
          alt={`사진대지 ${index + 1}`}
          className="aspect-[4/3] w-full cursor-pointer border border-gray-200 object-cover"
          onClick={onPreview}
        />
        <span className="absolute bottom-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white">
          {index + 1}
        </span>
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow transition-transform active:scale-95 disabled:opacity-50"
        aria-label={`사진 ${index + 1} 삭제`}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

/** 삭제 확인 모달 */
function DeleteConfirmModal({
  isOpen,
  imageUrl,
  onConfirm,
  onCancel,
  disabled,
}: {
  isOpen: boolean;
  imageUrl: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
}) {
  // 뒤로가기 이벤트 핸들러 (Android 백버튼 / 헤더 뒤로가기)
  useEffect(() => {
    if (!isOpen) return;
    const handleCloseTopModal = (e: Event) => {
      if (!disabled) {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("closeTopModal", handleCloseTopModal);
    return () => document.removeEventListener("closeTopModal", handleCloseTopModal);
  }, [isOpen, disabled, onCancel]);

  if (!isOpen) return null;

  // createPortal로 document.body에 렌더링하여
  // 부모의 transform이 fixed 포지셔닝에 영향을 주지 않도록 함
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl">
        <h3 className="mb-3 text-center text-lg font-medium">사진 삭제</h3>
        <img
          src={imageUrl}
          alt="삭제할 사진"
          className="mx-auto mb-4 h-32 w-32 rounded-lg object-cover"
        />
        <p className="mb-4 text-center text-sm text-gray-600">이 사진을 삭제하시겠습니까?</p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={disabled}
            className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 active:bg-gray-100 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            className="flex-1 rounded-lg bg-red-500 py-2.5 text-sm font-medium text-white active:bg-red-600 disabled:opacity-50"
          >
            {disabled ? "삭제 중..." : "삭제"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function OtherPhotosTab({
  pictures,
  version,
  projectId,
  docKey,
  machineId,
  onUpdate,
  isLoading,
}: OtherPhotosTabProps) {
  const { showToast } = useToast();
  const isNative = Capacitor.isNativePlatform();

  // 업로드 상태
  const [uploading, setUploading] = useState(false);

  // 업로드 진행률 (연속 촬영 병렬 업로드 시)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  // 인앱 카메라 상태
  const [cameraOpen, setCameraOpen] = useState(false);

  // 삭제 확인 모달 상태
  const [deleteTarget, setDeleteTarget] = useState<{ index: number; url: string } | null>(null);

  // 미리보기 모달 상태
  const [previewTarget, setPreviewTarget] = useState<{ index: number; url: string } | null>(null);

  // GraphQL Mutation
  const [updatePictures, { loading: saving }] = useMutation<
    UpdateMachineDocumentPicturesData,
    UpdateMachineDocumentPicturesVariables
  >(UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION);

  // 사진 배열 업데이트 (서버 저장)
  const savePictures = useCallback(
    async (newPictures: string[]) => {
      try {
        const result = await updatePictures({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              pictures: newPictures,
              expectedVersion: version,
              clientMutationId: generateClientMutationId(),
            },
          },
        });

        if (result.data?.updateMachineDocumentPictures.success) {
          const newVersion = result.data.updateMachineDocumentPictures.newVersion ?? version + 1;
          onUpdate(newPictures, newVersion);
          return true;
        } else {
          const errorCode = result.data?.updateMachineDocumentPictures.errorCode;
          if (errorCode === "VERSION_CONFLICT") {
            showToast("다른 곳에서 수정되었습니다. 새로고침 후 다시 시도해주세요.", "error");
          } else {
            showToast(
              result.data?.updateMachineDocumentPictures.error || "저장에 실패했습니다",
              "error"
            );
          }
          return false;
        }
      } catch (error) {
        logger.error("사진대지 저장 오류:", error);
        showToast("네트워크 오류가 발생했습니다", "error");
        return false;
      }
    },
    [projectId, docKey, machineId, version, updatePictures, onUpdate]
  );

  // 사진 업로드 핸들러 (File 객체 직접 전송으로 메모리 효율성 개선)
  const handleUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        // File 객체를 직접 업로드 (base64 변환 제거로 메모리 절약)
        const result = await uploadOtherPhoto(file, {
          projectId,
          docKey,
          machineId,
        });

        if (result.success && result.url) {
          // 오프라인 저장인 경우 로컬 상태만 업데이트 (서버 mutation 스킵)
          if (result.isLocal) {
            const newPictures = [...pictures, result.url];
            onUpdate(newPictures, version); // 로컬 상태만 업데이트
            showToast("오프라인: 사진이 임시 저장되었습니다", "info");
          } else {
            // 온라인: 서버에 pictures 배열 업데이트
            const newPictures = [...pictures, result.url];
            const saved = await savePictures(newPictures);
            if (saved) {
              showToast("사진이 등록되었습니다", "success");
            }
          }
        } else {
          showToast(result.error || "업로드에 실패했습니다", "error");
        }
      } catch (error) {
        logger.error("사진대지 업로드 오류:", error);
        showToast("업로드 중 오류가 발생했습니다", "error");
      } finally {
        setUploading(false);
      }
    },
    [pictures, projectId, docKey, machineId, version, onUpdate, savePictures, showToast]
  );

  // 배치 업로드 결과 처리 공통 로직 (결과 분류 → 로컬 반영 → 서버 저장 → 토스트)
  const processBatchUploadResults = useCallback(
    async (uploadResults: PromiseSettledResult<UploadResult>[]) => {
      // 결과 분류 (성공 URL / 로컬 URL / 실패)
      const onlineUrls: string[] = [];
      const localUrls: string[] = [];
      let failCount = 0;

      for (const settled of uploadResults) {
        if (settled.status === "rejected") {
          logger.error("사진대지 업로드 오류:", settled.reason);
          failCount++;
          continue;
        }
        const result = settled.value;
        if (result.success && result.url) {
          if (result.isLocal) {
            localUrls.push(result.url);
          } else {
            onlineUrls.push(result.url);
          }
        } else {
          failCount++;
        }
      }

      // 오프라인 저장분 로컬 상태 반영
      let currentPictures = [...pictures];
      let currentVersion = version;

      if (localUrls.length > 0) {
        currentPictures = [...currentPictures, ...localUrls];
        onUpdate(currentPictures, currentVersion);
      }

      // 온라인 업로드 성공분을 한 번의 mutation으로 서버에 저장
      if (onlineUrls.length > 0) {
        const serverPictures = [...pictures.filter((url) => !isLocalUrl(url)), ...onlineUrls];
        currentPictures = [...currentPictures, ...onlineUrls];

        try {
          const mutationResult = await updatePictures({
            variables: {
              input: {
                projectId,
                docKey,
                machineId,
                pictures: serverPictures,
                expectedVersion: currentVersion,
                clientMutationId: generateClientMutationId(),
              },
            },
          });

          if (mutationResult.data?.updateMachineDocumentPictures.success) {
            currentVersion =
              mutationResult.data.updateMachineDocumentPictures.newVersion ?? currentVersion + 1;
          } else {
            const errorCode = mutationResult.data?.updateMachineDocumentPictures.errorCode;
            if (errorCode === "VERSION_CONFLICT") {
              showToast("버전 충돌이 발생했습니다. 새로고침 후 다시 시도해주세요.", "error");
            } else {
              showToast(
                mutationResult.data?.updateMachineDocumentPictures.error || "저장에 실패했습니다",
                "error"
              );
            }
          }
        } catch (error) {
          logger.error("사진대지 일괄 저장 오류:", error);
          showToast("네트워크 오류가 발생했습니다", "error");
        }

        onUpdate(currentPictures, currentVersion);
      }

      setUploading(false);
      setUploadProgress(null);

      const successCount = onlineUrls.length + localUrls.length;
      if (successCount > 0) {
        showToast(`${successCount}장의 사진이 등록되었습니다`, "success");
      }
      if (failCount > 0) {
        showToast(`${failCount}장의 사진 업로드에 실패했습니다`, "error");
      }
    },
    [pictures, version, projectId, docKey, machineId, updatePictures, onUpdate, showToast]
  );

  // 갤러리 다중 선택 업로드 핸들러
  const handleMultiUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      if (files.length > MAX_GALLERY_SELECT) {
        showToast(`한 번에 최대 ${MAX_GALLERY_SELECT}장까지 선택할 수 있습니다`, "warning");
        return;
      }

      setUploading(true);
      setUploadProgress({ current: 0, total: files.length });

      let completedCount = 0;

      const uploadResults = await mapSettledWithConcurrency<File, UploadResult>(
        files,
        async (file: File) => {
          const result = await uploadOtherPhoto(file, { projectId, docKey, machineId });
          completedCount++;
          setUploadProgress({ current: completedCount, total: files.length });
          return result;
        },
        UPLOAD_CONCURRENCY
      );

      await processBatchUploadResults(uploadResults);
    },
    [projectId, docKey, machineId, processBatchUploadResults, showToast]
  );

  // 연속 촬영 시 base64를 큐에 모아두기
  const captureQueueRef = useRef<string[]>([]);

  // 인앱 카메라 촬영 완료 핸들러 (큐에 추가만)
  const handleCameraCapture = useCallback((base64: string) => {
    captureQueueRef.current.push(base64);
  }, []);

  // 인앱 카메라 열기
  const handleCameraOpen = useCallback(() => {
    captureQueueRef.current = [];
    setCameraOpen(true);
  }, []);

  // 인앱 카메라 닫기 → 큐에 모인 사진들을 병렬 업로드 (concurrency 3)
  const handleCameraClose = useCallback(async () => {
    setCameraOpen(false);
    const queue = captureQueueRef.current;
    captureQueueRef.current = [];

    if (queue.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: queue.length });

    let completedCount = 0;

    const uploadResults = await mapSettledWithConcurrency<string, UploadResult>(
      queue,
      async (base64: string) => {
        const file = base64ToFile(base64, generatePhotoFileName());
        const result = await uploadOtherPhoto(file, { projectId, docKey, machineId });
        completedCount++;
        setUploadProgress({ current: completedCount, total: queue.length });
        return result;
      },
      UPLOAD_CONCURRENCY
    );

    await processBatchUploadResults(uploadResults);
  }, [projectId, docKey, machineId, processBatchUploadResults]);

  // 삭제 확인 핸들러
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    const newPictures = pictures.filter((_, i) => i !== deleteTarget.index);
    const success = await savePictures(newPictures);
    if (success) {
      setDeleteTarget(null);
      showToast("사진이 삭제되었습니다", "success");
    }
  }, [deleteTarget, pictures, savePictures, showToast]);

  const disabled = uploading || saving;

  // 로딩 상태
  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <>
      <div className="flex flex-col bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b bg-white px-4 py-3">
          <h3 className="text-sm font-medium text-gray-800">사진대지 ({pictures.length})</h3>
          {disabled && (
            <div className="flex items-center gap-2">
              {uploadProgress && (
                <span className="text-xs text-blue-600">
                  {uploadProgress.current}/{uploadProgress.total}
                </span>
              )}
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            </div>
          )}
        </div>

        {/* 콘텐츠 */}
        <div className="flex-1 p-4">
          {pictures.length === 0 ? (
            <EmptyState
              onFileSelect={handleUpload}
              onCameraOpen={isNative ? handleCameraOpen : undefined}
              uploading={uploading}
              disabled={disabled}
              onMultiFileSelect={handleMultiUpload}
              galleryMultiple
            />
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {pictures.map((url, index) => (
                <PhotoCard
                  key={`${url}-${index}`}
                  url={url}
                  index={index}
                  onDelete={() => setDeleteTarget({ index, url })}
                  onPreview={() => setPreviewTarget({ index, url })}
                  disabled={disabled}
                />
              ))}
              <PhotoCaptureSlot
                onFileSelect={handleUpload}
                onCameraOpen={isNative ? handleCameraOpen : undefined}
                uploading={disabled}
                disabled={disabled}
                onMultiFileSelect={handleMultiUpload}
                galleryMultiple
              />
            </div>
          )}
        </div>

        {/* 삭제 확인 모달 */}
        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          imageUrl={deleteTarget?.url || ""}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          disabled={saving}
        />

        {/* 이미지 미리보기 모달 */}
        <ImagePreviewModal
          isOpen={!!previewTarget}
          imageUrl={previewTarget?.url || ""}
          label={`사진대지 ${(previewTarget?.index ?? 0) + 1}`}
          onClose={() => setPreviewTarget(null)}
        />
      </div>

      {/* 인앱 카메라 (네이티브 환경에서만, 연속 촬영 모드) */}
      {isNative && (
        <InAppCamera
          isOpen={cameraOpen}
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}
    </>
  );
}

export default OtherPhotosTab;
